import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Stack, TextField, Tooltip, Typography,
} from "@mui/material";

import {
  MAX_YEARS, curveFrom, describe as describeCurve, linearRetention, validate,
} from "../../utils/degradationCurve";
import { useTranslateText } from "../../context/LanguageContext";

const decimal = (value) => {
  const out = Number(String(value ?? "").trim().replace(",", "."));
  return String(value ?? "").trim() !== "" && Number.isFinite(out) ? out : null;
};

/** Retention in the last year of a straight-line curve, or null. */
export function finalRetention(firstYearPct, annualPct, years) {
  const first = decimal(firstYearPct); const rate = decimal(annualPct); const count = Math.trunc(decimal(years) ?? 0);
  if (first === null || rate === null || count < 1) return null;
  return 100 - first - rate * (count - 1);
}

/** The annual rate that reaches `finalPct` in the last year, or null. */
export function annualRateFor(firstYearPct, finalPct, years) {
  const first = decimal(firstYearPct); const last = decimal(finalPct); const count = Math.trunc(decimal(years) ?? 0);
  if (first === null || last === null || count < 2) return null;
  const rate = (100 - first - last) / (count - 1);
  return rate >= 0 ? rate : null;
}

/**
 * The module's warranted degradation, entered year by year and drawn.
 *
 * A single "0.2 %/year" was doing two jobs badly: standing for the first year,
 * which is not ageing at all but light-induced degradation -- a one-off drop in
 * the first few hundred hours -- and for every year after, which really is a
 * slow linear decline. Every datasheet states the two separately and guarantees
 * a retention at year 25 or 30 that the pair has to reach.
 *
 * So the curve is a TABLE, one row per year, holding retention as a percentage
 * of nameplate -- the figure the warranty page prints, rather than a per-year
 * loss the reader would have to difference in their head. The two headline
 * numbers fill it in one click, and any row can then be edited, because some
 * manufacturers publish a stepped curve that no pair of numbers describes.
 *
 * The plot is beside the table and not instead of it. Thirty typed rows are
 * thirty chances for a digit to go astray, and a transposed pair leaves the
 * endpoint correct -- so a warranty check will not catch it, and the eye will.
 */

// Room for an axis title on each side. Without them the reader has to infer
// from the numbers which axis is years and which is percent -- and on a chart
// whose whole job is to be checked against a datasheet, inferring is the thing
// to remove.
const AXIS = { top: 14, right: 14, bottom: 46, left: 56 };
const PLOT = { width: 420, height: 300 };

/**
 * Year ticks: always the first and the last, and a round step between.
 *
 * The first and last matter because they are the two figures a warranty states.
 * The step is chosen to land on 5s and 10s where it can -- "1, 5, 10, 15" reads
 * as years, "1, 7, 13, 19" reads as an accident.
 */
function yearTicksFor(lastYear) {
  if (lastYear <= 1) return [1];
  const step = [1, 2, 5, 10, 20].find((candidate) => lastYear / candidate <= 7) || 25;
  const ticks = [1];
  for (let year = step; year < lastYear; year += step) {
    if (year > 1) ticks.push(year);
  }
  // Drop a penultimate tick that would crowd the last one.
  if (ticks.length > 1 && lastYear - ticks[ticks.length - 1] < step * 0.6) ticks.pop();
  ticks.push(lastYear);
  return ticks;
}

function CurvePlot({ retention, lifetime }) {
  const curve = curveFrom(retention);
  const width = PLOT.width;
  const height = PLOT.height;
  const innerW = width - AXIS.left - AXIS.right;
  const innerH = height - AXIS.top - AXIS.bottom;

  if (!curve || curve.length < 2) {
    return (
      <Box
        sx={{
          width: "100%", maxWidth: width, height, display: "flex",
          alignItems: "center", justifyContent: "center",
          border: "1px dashed", borderColor: "divider", borderRadius: 1.5,
          color: "text.secondary", textAlign: "center", px: 2,
        }}
      >
        <Typography variant="caption">
          The plot appears once the table holds at least two valid years.
        </Typography>
      </Box>
    );
  }

  const lastYear = curve.length;
  // The vertical range is the curve's own, padded, not 0-100: a warranty that
  // runs 99% to 87% drawn against a zero baseline is a flat line, and a flat
  // line is exactly the thing this plot exists to contradict.
  const lo = Math.floor(Math.min(...curve) - 1);
  const hi = Math.ceil(Math.max(...curve, 100) );
  const x = (year) => AXIS.left + ((year - 1) / Math.max(1, lastYear - 1)) * innerW;
  const y = (value) => AXIS.top + (1 - (value - lo) / Math.max(1e-9, hi - lo)) * innerH;

  const path = curve.map((value, index) => `${index ? "L" : "M"}${x(index + 1).toFixed(1)},${y(value).toFixed(1)}`).join(" ");
  const area = `${path} L${x(lastYear).toFixed(1)},${(AXIS.top + innerH).toFixed(1)} L${x(1).toFixed(1)},${(AXIS.top + innerH).toFixed(1)} Z`;

  const ticks = [lo, (lo + hi) / 2, hi];
  const yearTicks = yearTicksFor(lastYear);
  const baseline = AXIS.top + innerH;

  return (
    <Box sx={{ color: "text.secondary" }}>
      <svg
        width={width} height={height} role="img"
        aria-label="Retention against nameplate, year by year"
      >
        {ticks.map((value) => (
          <g key={value}>
            <line
              x1={AXIS.left} x2={width - AXIS.right} y1={y(value)} y2={y(value)}
              stroke="currentColor" strokeOpacity={0.18} strokeWidth={1}
            />
            <text x={AXIS.left - 8} y={y(value) + 3.5} textAnchor="end" fontSize={11} fill="currentColor">
              {value.toFixed(0)}
            </text>
          </g>
        ))}

        {/* A tick MARK as well as a label on each year: the label alone floats
            under the plot and leaves the reader measuring by eye which point it
            belongs to. */}
        {yearTicks.map((year) => (
          <g key={year}>
            <line
              x1={x(year)} x2={x(year)} y1={baseline} y2={baseline + 4}
              stroke="currentColor" strokeOpacity={0.45} strokeWidth={1}
            />
            <text x={x(year)} y={baseline + 16} textAnchor="middle" fontSize={11} fill="currentColor">
              {year}
            </text>
          </g>
        ))}

        <line
          x1={AXIS.left} x2={width - AXIS.right} y1={baseline} y2={baseline}
          stroke="currentColor" strokeOpacity={0.35} strokeWidth={1}
        />

        <path d={area} fill="currentColor" fillOpacity={0.07} stroke="none" />
        <path d={path} fill="none" stroke="#1976d2" strokeWidth={2} strokeLinejoin="round" />
        {/* The end of the table, which is the figure a lender asks about. */}
        <circle cx={x(lastYear)} cy={y(curve[lastYear - 1])} r={3.5} fill="#1976d2" />
        {/* Where the project actually ends, when that is inside the table. */}
        {lifetime && lifetime < lastYear ? (
          <line
            x1={x(lifetime)} x2={x(lifetime)} y1={AXIS.top} y2={baseline}
            stroke="currentColor" strokeOpacity={0.4} strokeDasharray="3 3" strokeWidth={1}
          />
        ) : null}

        {/* Axis titles, on the axes rather than in a caption underneath: a
            caption describes the picture, a title names what you are reading. */}
        <text
          x={AXIS.left + innerW / 2} y={height - 6} textAnchor="middle"
          fontSize={11} fontWeight={600} fill="currentColor"
        >
          Year
        </text>
        <text
          x={14} y={AXIS.top + innerH / 2} textAnchor="middle"
          fontSize={11} fontWeight={600} fill="currentColor"
          transform={`rotate(-90 14 ${AXIS.top + innerH / 2})`}
        >
          Retention (% of nameplate)
        </text>
      </svg>
    </Box>
  );
}

export default function DegradationCurveDialog({
  open, onClose, retention, lifetimeYears = 30, moduleName = "", onSave,
}) {
  const translateText = useTranslateText();
  const [rows, setRows] = useState([]);
  const [firstYear, setFirstYear] = useState("1.0");
  const [annual, setAnnual] = useState("0.40");
  const [years, setYears] = useState(String(lifetimeYears || 30));
  // The warranted retention in the last year: a third way to state the same
  // straight line. Typing it solves for the annual rate; typing the rate or
  // the years recomputes it.
  const [finalYear, setFinalYear] = useState("");
  const [finalTyped, setFinalTyped] = useState(false);

  // Reloaded on every open, so a dialog cancelled halfway does not leave its
  // edits behind for the next time it is opened.
  useEffect(() => {
    if (!open) return;
    const start = Array.isArray(retention) && retention.length
      ? retention.map((value) => String(value))
      : linearRetention(1.0, 0.4, lifetimeYears || 30).map((value) => String(value));
    setRows(start);
    setFinalTyped(false);
    setYears(String(start.length || lifetimeYears || 30));
    const summary = describeCurve(start);
    if (summary) {
      setFirstYear(summary.firstYearPct.toFixed(2));
      setAnnual(summary.annualPct.toFixed(2));
      setFinalYear(summary.finalRetentionPct.toFixed(2));
    }
  }, [open, retention, lifetimeYears]);

  const problems = useMemo(() => validate(rows), [rows]);
  const summary = useMemo(() => describeCurve(rows), [rows]);
  const byYear = useMemo(() => {
    const map = new Map();
    problems.forEach((problem) => {
      if (problem.year && !map.has(problem.year)) map.set(problem.year, problem.message);
    });
    return map;
  }, [problems]);

  const updateFinal = (first, rate, count) => {
    const value = finalRetention(first, rate, count);
    if (value !== null) setFinalYear(value.toFixed(2));
  };
  // The field typed last decides: a typed final year holds while the first
  // year or the years change, and the rate follows it; otherwise the final
  // year follows the rate.
  const fromFinal = (first, count) => {
    const rate = annualRateFor(first, finalYear, count);
    if (rate !== null) setAnnual(rate.toFixed(4).replace(/0{1,2}$/, ""));
  };
  const changeFirst = (value) => { setFirstYear(value); if (finalTyped) fromFinal(value, years); else updateFinal(value, annual, years); };
  const changeAnnual = (value) => { setFinalTyped(false); setAnnual(value); updateFinal(firstYear, value, years); };
  const changeYears = (value) => { setYears(value); if (finalTyped) fromFinal(firstYear, value); else updateFinal(firstYear, annual, value); };
  const changeFinal = (value) => {
    setFinalTyped(true);
    setFinalYear(value);
    const rate = annualRateFor(firstYear, value, years);
    if (rate !== null) setAnnual(rate.toFixed(4).replace(/0{1,2}$/, ""));
  };

  const fill = () => {
    const count = Number(years) || lifetimeYears || 30;
    // A typed final year is met exactly, not through the rounded rate shown.
    const exact = finalTyped ? annualRateFor(firstYear, finalYear, count) : null;
    const filled = linearRetention(firstYear, exact ?? annual, count);
    setRows(filled.map((value) => String(value)));
  };

  const setRow = (index, value) => {
    setRows((current) => {
      const next = current.slice();
      next[index] = value;
      return next;
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
            PaperProps={{ sx: { maxWidth: 760 } }}>
      <DialogTitle>
        Degradation curve{moduleName ? ` – ${moduleName}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.75 }}>
          Retention warranted by the manufacturer, as a percentage of nameplate.
          The first year is light-induced degradation and is applied inside the
          hourly simulation; every year after it scales the year-by-year
          projection.
        </Typography>

        {/* The two datasheet numbers, which fill the table rather than replace
            it. Nobody should type thirty rows to describe a straight line. */}
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small" label="First year" value={firstYear}
            onChange={(event) => changeFirst(event.target.value)}
            InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: "text.secondary" }}>%</Typography> }}
            sx={{ width: 118 }}
          />
          <TextField
            size="small" label="Thereafter" value={annual}
            onChange={(event) => changeAnnual(event.target.value)}
            InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>% / yr</Typography> }}
            sx={{ width: 128 }}
          />
          <TextField
            size="small" label="Years" value={years} type="number"
            onChange={(event) => changeYears(event.target.value)}
            inputProps={{ min: 1, max: MAX_YEARS }}
            sx={{ width: 96 }}
          />
          <TextField
            size="small" label="Final year retention" value={finalYear}
            onChange={(event) => changeFinal(event.target.value)}
            InputProps={{ endAdornment: <Typography variant="caption" sx={{ color: "text.secondary" }}>%</Typography> }}
            inputProps={{ "aria-label": "Retention in the final year" }}
            sx={{ width: 190 }}
          />
          <Tooltip title={translateText("Overwrite every row from these three numbers")}>
            <span>
              <Button variant="outlined" size="small" onClick={fill} sx={{ mt: 0.4, textTransform: "none" }}>
                Fill table
              </Button>
            </span>
          </Tooltip>
        </Stack>

        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* The table is the record. Everything else on this screen is read
              out of it -- including the summary, so a row edited by hand after
              filling is never described by numbers it no longer has. */}
          <Box
            sx={{
              flex: "1 1 260px", minWidth: 250, maxHeight: 300, overflowY: "auto",
              border: "1px solid", borderColor: "divider", borderRadius: 1.5,
            }}
          >
            {/* An OPAQUE header. `action.hover` is a translucent overlay, so
                rows scrolling underneath showed straight through it and the
                header read as another row of data. A sticky header has to be
                solid or it is not a header. */}
            <Box sx={{
              display: "grid", gridTemplateColumns: "56px 1fr", gap: 0,
              position: "sticky", top: 0, zIndex: 2,
              bgcolor: "background.paper",
              boxShadow: (theme) => `inset 0 -1px 0 ${theme.palette.divider}, 0 2px 4px -2px ${theme.palette.divider}`,
            }}>
              <Typography variant="caption" sx={{ px: 1.25, py: 0.7, fontWeight: 700 }}>Year</Typography>
              <Typography variant="caption" sx={{ px: 1.25, py: 0.7, fontWeight: 700 }}>Retention %</Typography>
            </Box>
            {rows.map((value, index) => (
              <Box
                key={index}
                sx={{
                  display: "grid", gridTemplateColumns: "56px 1fr", alignItems: "center",
                  borderTop: index === 0 ? "none" : "1px solid", borderColor: "divider",
                }}
              >
                <Typography variant="body2" sx={{ px: 1.25, color: "text.secondary" }}>
                  {index + 1}
                </Typography>
                <TextField
                  variant="standard" size="small" value={value}
                  error={byYear.has(index + 1)}
                  helperText={translateText(byYear.get(index + 1) || "")}
                  onChange={(event) => setRow(index, event.target.value)}
                  inputProps={{
                    inputMode: "decimal",
                    "aria-label": `Retention in year ${index + 1}`,
                    style: { fontVariantNumeric: "tabular-nums", padding: "4px 0" },
                  }}
                  sx={{ px: 1.25, py: 0.15 }}
                />
              </Box>
            ))}
          </Box>

          <CurvePlot retention={rows} lifetime={Number(lifetimeYears) || null} />
        </Box>

        {problems.length ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {problems.length === 1
              ? translateText(problems[0].message)
              : `${problems.length} rows need attention; each is marked in the table.`}
          </Alert>
        ) : summary ? (
          <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
            {summary.uniform
              ? `${summary.firstYearPct.toFixed(2)}% in the first year, then ${summary.annualPct.toFixed(2)}% a year — `
              : "Edited by hand, so no single annual rate describes it — "}
            {summary.finalRetentionPct.toFixed(1)}% of nameplate remaining at year {summary.years}.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={problems.length > 0}
          onClick={() => onSave(curveFrom(rows))}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
